import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { IRADesignationListedTypes } from '@interfaces/models/ira-designation-types';
import { FilteredClientAccount, IRATypes } from '@shared/models';
import { RelationshipDesignationTypes } from '@shared/models/relationship-designation-types';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import { FeatureToggleState } from '@store/reducers/feature/feature.reducer';
import {
  getIraType,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
} from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import { canChooseExisting } from '@store/selectors/receipt/inheritance/cashier-ira';
import { VgEmploymentState } from 'vg-interaction-model/dist';

import {
  getIsExecutor,
  getIsIndividualAccount,
  getIsIndividualExecutor,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRASingleExecutor,
  getIsIRASpouse,
  getIsIRASpouseExecutor,
  getIsIRATrust,
  getIsJointAccount,
  getIsOther,
  getIsProspect,
  getIsSpouse,
  getIsTrustToTrust,
  getIsTrustToTrustFlow,
  isIneligibleIRADesignation,
  TriageState,
} from '../../../reducers/triage/triage.reducer';

export const getInheritanceDecedentInfoRoute = (
  decedentInfoState: DecedentInfoState,
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
  individualAccounts: FilteredClientAccount,
  indvDesignationExecutor: boolean,
  isIRAToEstateFlow: boolean,
  isTrustFlow: boolean,
  isTrustToTrustFlow: boolean,
): string => {
  return decedentInfoState.isDeathVerified &&
    !indvDesignationExecutor &&
    !isIRAToEstateFlow &&
    !isTrustFlow &&
    !isTrustToTrustFlow
    ? getInheritanceUploadPageNextRoute(
        triageState,
        featureToggleState,
        individualAccounts,
        indvDesignationExecutor,
        isTrustToTrustFlow,
      )
    : 'secure/inheritance/upload';
};

export const getInheritanceAssumeNextPage = (
  triageState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
): string => {
  if (canChooseExisting(triageState, selectedAccounts)) {
    return 'secure/inheritance/transfer-to';
  }
  if (getIsProspect(triageState)) {
    return 'secure/inheritance/employment';
  }
  return 'secure/inheritance/personal-info';
};

export const getInheritanceOverviewNextPage = (isMultiInheritanceFlow: boolean): string => {
  if (isMultiInheritanceFlow) {
    return 'multi-inheritance/triage';
  }
  return 'inheritance/triage';
};

export const getInheritanceTriageNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
): string => {
  if (
    getIsOther(triageState) ||
    (getIsJointAccount(triageState) && getIsProspect(triageState)) ||
    isIneligibleIRADesignation(triageState, featureToggleState) ||
    IndividualDesignationListedTypes.NOTSURE === triageState.individualDesignation
  ) {
    return 'inheritance/ineligible';
  } else {
    return 'inheritance/summary';
  }
};

export const getInheritanceUploadNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
  individualAccounts: FilteredClientAccount,
  indvDesignationExecutor: boolean,
  isTrustToTrustFlow: boolean,
): string =>
  getInheritanceUploadPageNextRoute(
    triageState,
    featureToggleState,
    individualAccounts,
    indvDesignationExecutor,
    isTrustToTrustFlow,
  );

// This function returns the next route assuming user is on the upload page.
// Also being used to skip the upload page from decedent-info. Not ideal.
export function getInheritanceUploadPageNextRoute(
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
  individualAccounts: FilteredClientAccount,
  indvDesignationExecutor: boolean,
  isTrustToTrustFlow: boolean,
): string {
  if (indvDesignationExecutor) {
    return 'secure/inheritance/estate';
  }
  if (isTrustToTrustFlow) {
    return 'secure/inheritance/tcf-info-trust';
  }
  if (getIsIRAAccount(triageState)) {
    if (getIsSpouse(triageState)) {
      return 'secure/inheritance/ira-type';
    } else {
      if (
        featureToggleState.benes_by_relation &&
        IRADesignationListedTypes.RELATIONSHIP === triageState.IRADesignation
      ) {
        return 'secure/inheritance/beneficiaries/relationship';
      } else {
        return 'secure/inheritance/ira-type';
      }
    }
  }
  if (getIsJointAccount(triageState)) {
    return 'secure/inheritance/transfer-from';
  }
  if (getIsIndividualAccount(triageState)) {
    if (getIsProspect(triageState)) {
      return 'secure/inheritance/employment';
    }
    if (individualAccounts.accounts.length > 0) {
      return 'secure/inheritance/transfer-to';
    } else {
      return 'secure/inheritance/personal-info';
    }
  }
  if (getIsTrustToTrust(triageState)) {
    return 'secure/inheritance/trust-info';
  }
  return 'error';
}

export const getInheritanceEstateNextPage = (triageState: TriageState): string =>
  getIsProspect(triageState) || getIsIRASingleExecutor(triageState)
    ? 'secure/inheritance/employment'
    : 'secure/inheritance/personal-info';

export const getInheritancePersonalInfoNextPage = (): string => 'secure/inheritance/contact-info';

export const getInheritanceContactInfoNextPage = (
  isIRASingleExecutor: boolean,
  isTrustee: boolean,
  isTrustToTrustFlow: boolean,
  isClient: boolean,
): string => {
  if (isIRASingleExecutor || isTrustToTrustFlow || (isTrustee && isClient)) {
    return 'secure/inheritance/decedent-info';
  }
  return 'secure/inheritance/employment';
};
export const getInheritanceEmploymentNextPage = (
  triage: TriageState,
  employment: VgEmploymentState,
): string => {
  const isIRAExecutor = getIsIRASpouseExecutor(triage) || getIsIRAExecutor(triage);
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triage);
  const isIRATrust = getIsIRATrust(triage);
  const isIndividualExecutor = getIsIndividualExecutor(triage);
  const { associated, spouse } = employment.associations;
  const { employmentStatus } = employment.employmentStatus;

  if (
    (isIRAExecutor || isTrustToTrustFlow || isIRATrust || isIndividualExecutor) &&
    associated &&
    !spouse &&
    employmentStatus !== 'VE'
  ) {
    return 'secure/inheritance/upload/letter';
  }

  if (getIsExecutor(triage) || getIsIRATrust(triage) || isTrustToTrustFlow) {
    return 'secure/inheritance/investment-objectives';
  }

  return 'secure/inheritance/income-net-worth';
};
export const getInheritanceLetterUploadNexPage = (): string =>
  'secure/inheritance/investment-objectives';
export const getInheritanceIncomeNetWorthNextPage = (): string =>
  'secure/inheritance/investment-objectives';
export const getInheritanceInvestObjNextPage = (): string => 'secure/inheritance/review';
export const getInheritanceReviewNextPage = (
  selectedAccounts: ISelectedClientAccountsState,
): string =>
  selectedAccounts.transactionType === 'EXISTING'
    ? 'secure/inheritance/terms/existing'
    : 'secure/inheritance/terms/new';

export const getInheritanceTermsNextPage = (): string => {
  return 'secure/inheritance/confirm';
};

export const getInheritanceTransferFromNextPage = (
  individualAccounts: FilteredClientAccount,
): string =>
  individualAccounts.accounts.length > 0
    ? 'secure/inheritance/transfer-to'
    : 'secure/inheritance/personal-info';

export const getInheritanceTransferToNextPage = (
  selectedAccounts: ISelectedClientAccountsState,
): string =>
  selectedAccounts.transactionType === TransactionTypesEnum.EXISTING
    ? 'secure/inheritance/review'
    : 'secure/inheritance/personal-info';

export const getInheritanceBeneRelationshipNextPage = (
  beneficiariesState: BeneficiariesState,
): string =>
  RelationshipDesignationTypes.NAMED === beneficiariesState.relationshipDesignation
    ? 'inheritance/ineligible'
    : 'secure/inheritance/beneficiaries/details';

export const getInheritanceBeneDetailsNextPage = (): string => 'secure/inheritance/ira-type';

export const getInheritanceIraTypeNextPage = (
  triageState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
): string => {
  if (
    getIsIRASpouse(triageState) &&
    [IRATypes.OTHER, IRATypes.ROTH].includes(getIraType(selectedAccounts))
  ) {
    return 'secure/inheritance/assume';
  }
  if (getIsIRASingleExecutor(triageState)) {
    return 'secure/inheritance/estate';
  }
  if (getIsIRATrust(triageState)) {
    return 'secure/inheritance/trust-info';
  }
  if (getIsProspect(triageState)) {
    return 'secure/inheritance/employment';
  }
  return 'secure/inheritance/personal-info';
};

export const getInheritanceTrustInfoNextPage = (): string => {
  return 'secure/inheritance/trust-tax-info';
};

export const getInheritanceTrustTaxInfoNextPage = (): string => {
  return 'secure/inheritance/employment';
};

export const getInheritanceTCFTrustInfoNextPage = (): string => {
  return 'secure/inheritance/tcf-sign-trust';
};

export const getInheritanceTCFTrustSignNextPage = (): string => {
  return 'secure/inheritance/trust-info';
};
